import React from "react";
import { Organization, WithContext } from "schema-dts";

import {
    ThelabuiTypesAlternatePage as AlternatePage,
    Context,
    ThelabuiTypesLanguage as Language,
    reverse,
} from "@reactivated";

import { MetaTagsBlock } from "@thelabnyc/thelabui/src/components/MetaTagsBlock";
import { unique } from "@thelabnyc/thelabui/src/utils/functional";

import { ReactivatedContext } from "../../context";
import { FooterNavigation } from "../FooterNav";
import { HeaderContent, HeaderNav } from "../HeaderNav";
import { PageNavWrapper } from "../PageNav";
import { ScrollDown } from "../ScrollDown";
import { SwitchLocaleModalWithContext } from "../SwitchLocaleModal";

import headerStyles from "../HeaderNav/Header.module.scss";

import "../../styles/global.scss";

interface Props {
    title: string;
    seo_title?: string;
    search_description?: string;
    language: Language | null;
    alternate_pages: AlternatePage[];
    robots?: string;
    rootClassName?: string;
    children?: React.ReactNode;
    /** Override header data (used by snippet preview) */
    headerOverride?: HeaderContent;
    /** Whether the header renders transparently over the first body block */
    overlapNav?: boolean;
    /** Override the canonical URL (defaults to context.request.url) */
    canonicalUrl?: string;
    /** Open Graph title */
    ogTitle?: string;
    /** Open Graph description */
    ogDescription?: string;
    /** Open Graph URL */
    ogUrl?: string;
}

/**
 * Not sure if this needs to be a separate component but it's separating concerns
 * and has room to grow this way
 *
 * SEO asked for it sitewide even though Google's guidelines suggest otherwise
 */
const JsonLD = ({ logoUrl }: { logoUrl: string }) => {
    const jsonLD: WithContext<Organization> = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "url": "https://www.sksappliances.com/",
        "logo": logoUrl,
        "name": "SKS",
        "alternateName": "Signature Kitchen Suite",
        "description":
            "SKS is the luxury appliance brand from LG Electronics USA, offering high-end, built-in kitchen appliances designed for Technicureans™.",
        "sameAs": [
            "https://www.facebook.com/SKSappliances/",
            "https://www.instagram.com/sksappliances/",
            "https://www.linkedin.com/company/sksappliances/",
        ],
        "email": "concierge@skscares.com",
        "telephone": "+1-800-243-0000",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "1000 Sylvan Ave",
            "addressLocality": "Englewood Cliffs",
            "addressRegion": "NJ",
            "postalCode": "07632",
            "addressCountry": "US",
        },
        "parentOrganization": {
            "@type": "Organization",
            "name": "LG Electronics USA",
        },
    };
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLD) }}
        ></script>
    );
};

/**
 * We had a coherent way to map locales defined in Wagail to <link> tags with
 * hreflangs, until they asked to include all of the locale work that the
 * Italian site did. We didn't think it a good idea to add a bunch of new locales
 * to Wagtail just to do this one task, so we're trying to do as little
 * hardcoding as possible to achieve the task without undoing current work
 */
const europeanVariants = [
    { hrefLang: "es-ES", href: "/es/home-espanol/" },
    { hrefLang: "nl-NL", href: "/nl/home-nederlands/" },
    { hrefLang: "fr-FR", href: "/fr/home-french/" },
    { hrefLang: "de-DE", href: "/de/home-deutsch/" },
    { hrefLang: "sk-SK", href: "/sk/home-slovencina/" },
    { hrefLang: "pl-PL", href: "/pl/home-polski/" },
    { hrefLang: "cs-CZ", href: "/cz/home-cz/" },
    { hrefLang: "en-GB", href: "/en/home/" },
];

export const PageSkeleton = (props: Props) => {
    const context = React.useContext(Context);
    const rootTitle = context.root_page.seo_title || context.root_page.title;
    const titleParts = [props.seo_title || props.title, rootTitle]
        .filter(unique)
        .join(" | ");

    // If we ever refactor to let child page templates put stuff in <head>,
    // we could do this differently
    const isHomepage = context.template_name === "HomePageTemplate";

    // Get additional countries not already covered by Wagtail locales
    // (functionally, this is to use any live legacy sites remaining)
    const additionalCountries = isHomepage
        ? context.countries.filter(
              (country) =>
                  !props.alternate_pages.some(
                      (alt) => alt.language.code === country.language_code,
                  ),
          )
        : [];

    /**
     * The legacy Italian site has its own set of locales that we want to include
     */
    const italySiteExists = additionalCountries.find(
        (alt) => alt.language_code === "it-it",
    );

    const alternatePages = [
        // Wagtail-managed locales
        ...props.alternate_pages.map((alt) => ({
            hrefLang: alt.language.code,
            href: alt.url,
        })),
        // Legacy sites that are still live
        ...additionalCountries.map((country) => ({
            hrefLang: country.language_code,
            // country.url was just `/en-us/` for en-us and that's no good
            href: country.url.startsWith("/")
                ? `${new URL(context.request.url).origin}${country.url}`
                : country.url,
        })),
        // Legacy Italian site's extra work
        ...(italySiteExists
            ? europeanVariants.map((variant) => ({
                  hrefLang: variant.hrefLang,
                  href: `${new URL(italySiteExists.url).origin}${variant.href}`,
              }))
            : []),
    ];
    return (
        <html
            lang={props.language?.code}
            dir={props.language?.dir}
            data-design-version={context.DESIGN_VERSION ?? "v2024"}
        >
            <head>
                <meta charSet="utf-8" />
                <title>{titleParts}</title>
                {props.search_description && (
                    <meta
                        name="description"
                        content={props.search_description}
                    />
                )}
                {props.robots && <meta name="robots" content={props.robots} />}
                {MetaTagsBlock({
                    tags: context.meta_tags.meta_tags.value,
                })}
                <meta
                    name="viewport"
                    content={
                        context.DESIGN_VERSION === "v2026"
                            ? "width=device-width, initial-scale=1, viewport-fit=cover"
                            : "width=device-width, initial-scale=1"
                    }
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="96x96"
                    href={context.core_assets.favicon_png}
                />
                <link
                    rel="icon"
                    type="image/svg+xml"
                    href={context.core_assets.favicon_svg}
                />
                <link
                    rel="shortcut icon"
                    href={context.core_assets.favicon_ico}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href={context.core_assets.apple_touch_icon}
                />
                <meta name="apple-mobile-web-app-title" content={rootTitle} />
                <link
                    rel="manifest"
                    href={reverse("cms-api:pwa-site-manifest")}
                />
                <link
                    rel="canonical"
                    href={props.canonicalUrl ?? context.request.url}
                />
                {props.ogTitle && (
                    <meta property="og:title" content={props.ogTitle} />
                )}
                {props.ogDescription && (
                    <meta
                        property="og:description"
                        content={props.ogDescription}
                    />
                )}
                {props.ogUrl && (
                    <meta property="og:url" content={props.ogUrl} />
                )}
                {isHomepage && (
                    <link
                        rel="alternate"
                        hrefLang="x-default"
                        href={
                            context.root_page.url.startsWith("/")
                                ? `${new URL(context.request.url).origin}${context.root_page.url}`
                                : context.root_page.url
                        }
                    />
                )}
                {alternatePages.map((page) => (
                    <link key={page.hrefLang} rel="alternate" {...page} />
                ))}
                <JsonLD
                    logoUrl={`${new URL(context.request.url).origin}${context.core_assets.logo}`}
                />
            </head>
            <body>
                <ReactivatedContext.Provider value={context}>
                    <SwitchLocaleModalWithContext />
                    {context.DESIGN_VERSION === "v2026" ? (
                        <HeaderNav
                            headerOverride={props.headerOverride}
                            overlapNav={props.overlapNav}
                        />
                    ) : (
                        <>
                            <ScrollDown />
                            <PageNavWrapper nav={context.nav} />
                        </>
                    )}
                    <main
                        id={headerStyles.mainContentId}
                        className={props.rootClassName}
                    >
                        {props.children}
                    </main>
                    {context.footer?.body.value && (
                        <FooterNavigation value={context.footer?.body.value} />
                    )}
                </ReactivatedContext.Provider>
            </body>
        </html>
    );
};
